import type { SimplePrintLine } from '@/print-engine/adapters/simple-document';
import { formatMoney } from '@/utils/format';

export type AssemblyEstimatePrintDetail = {
  estimate: Record<string, unknown>;
  nodes: Array<Record<string, unknown>>;
  materials?: Array<Record<string, unknown>>;
  operations?: Array<Record<string, unknown>>;
  stack?: Record<string, number>;
  statements?: Array<Record<string, unknown>>;
};

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function formatAssemblyPrintAmount(amount: unknown, currency: string): string {
  return formatMoney(toNum(amount), {
    currency: currency || 'AED',
    locale: 'en-US',
    display: 'code_prefix',
    separator: ' ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Top-level quotation assemblies as print lines (with leaf cost roll-up summary). */
export function buildAssemblyEstimatePrintLines(detail: AssemblyEstimatePrintDetail): SimplePrintLine[] {
  const currency = String(detail.estimate.currency || 'AED');
  const nodes = detail.nodes || [];
  const tops = nodes
    .filter((n) => !n.parent_id && !n.is_phantom)
    .sort((a, b) => toNum(a.sequence_number) - toNum(b.sequence_number));

  return tops.map((node, idx) => {
    const title = String(node.node_name || 'Assembly');
    const strategy = String(node.strategy || '');
    const extras: string[] = [];
    if (node.description) extras.push(String(node.description));
    extras.push(`Strategy: ${strategy}${node.is_phantom ? ' · Phantom' : ''}`);
    const mat = toNum(node.material_cost);
    const eq = toNum(node.equipment_cost);
    const mp = toNum(node.manpower_cost);
    const sc = toNum(node.subcontract_cost);
    const tool = toNum(node.tooling_cost);
    const cats = [
      mat > 0 ? `Material ${formatAssemblyPrintAmount(mat, currency)}` : '',
      eq > 0 ? `Equipment ${formatAssemblyPrintAmount(eq, currency)}` : '',
      mp > 0 ? `Manpower ${formatAssemblyPrintAmount(mp, currency)}` : '',
      sc > 0 ? `Subcontract ${formatAssemblyPrintAmount(sc, currency)}` : '',
      tool > 0 ? `Tooling ${formatAssemblyPrintAmount(tool, currency)}` : '',
    ].filter(Boolean);
    if (cats.length) extras.push(`Categories: ${cats.join(' · ')}`);

    return {
      lineNumber: idx + 1,
      description: title,
      cells: {
        qty: String(node.quantity_per_parent ?? 1),
        uom: String(node.uom || 'EA'),
        strategy,
        direct: formatAssemblyPrintAmount(node.direct_cost, currency),
        estimated: formatAssemblyPrintAmount(node.estimated_cost, currency),
        selling: formatAssemblyPrintAmount(node.selling_price, currency),
      },
      extraLines: extras,
    };
  });
}

export function buildAssemblyEstimatePrintMeta(detail: AssemblyEstimatePrintDetail): Array<{ label: string; value: string }> {
  const e = detail.estimate;
  return [
    { label: 'Revision', value: String(e.revision_number ?? 1) },
    { label: 'Status', value: String(e.status || '') },
    { label: 'Customer', value: String(e.customer_name || '') },
    { label: 'Project', value: String(e.project_name || '') },
    { label: 'Cost basis', value: String(e.cost_basis_date || '') },
    { label: 'Currency', value: String(e.currency || 'AED') },
  ].filter((m) => m.value);
}
